import React, { useState, useEffect } from 'react';
import './footer.css';
import { getTranslations } from '../Utils';
import UserAgreementModal from './UserAgreementModal';
import axios from 'axios';

interface FooterProps {
  onTranslationsLoaded?: (translations: Record<string, string>) => void;
  onAgreementClick?: () => void;
}

const Footer: React.FC<FooterProps> = ({ onTranslationsLoaded, onAgreementClick }) => {
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [lang, setLang] = useState<string>("");
  const [showAgreement, setShowAgreement] = useState(false);

  const handleAgreementClick = () => {
    if (onAgreementClick) {
      onAgreementClick();
    } else {
      setShowAgreement(true);
    }
  };

  useEffect(() => {
    const loadTranslations = async () => {
      const initialLang = getLanguageFromCookies() ?? new Intl.Locale(navigator.language).language;
      setLang(initialLang);
      const loadedTranslations = await getTranslations(initialLang);
      setTranslations(loadedTranslations);
      onTranslationsLoaded?.(loadedTranslations); // Notify parent if callback provided
    };

    loadTranslations();
    getApplicationInfo();
  }, [onTranslationsLoaded]);

  const handleLangChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedLang = event.target.value;
    setLang(selectedLang);
    document.cookie = `lang=${selectedLang}; path=/`; // Update language in cookies
    const loadedTranslations = await getTranslations(selectedLang);
    setTranslations(loadedTranslations);
    onTranslationsLoaded?.(loadedTranslations); // Notify parent
  };

  const getLanguageFromCookies = (): string | null => {
    const match = /(?:^|;\s*)lang=([^;]*)/.exec(document.cookie);
    return match ? decodeURIComponent(match[1]) : null;
  };

  const getApplicationInfo = () => {
    axios.get("/api/info/application")
      .then(response => {
        const data = response.data;
        const version = document.getElementById('version-value') as HTMLLinkElement;
        const timestamp = document.getElementById('timestamp-value') as HTMLSpanElement;

        if (version && timestamp) {
          version.innerText = data.version;
          const datetime = new Date(data.timestamp);
          timestamp.innerText = datetime.toISOString().substring(0, 10);
        }
      })
      .catch(error => {
        const message = `${error.response?.status ?? "Unknown"} : ${error.response?.data ?? error.message}`;
        console.log("Cannot change interface language: " + message);
      });
  };

  return (
    <footer className="footer">
      <div>
        <div className="footer-content">
          <div id="version">{translations["footer.version"]}: <a href="/changelog" id="version-value">versionWillBeLoaded</a></div>
          <div id="timestamp">{translations["footer.build"]}: <span id="timestamp-value"></span></div>
        </div>
      </div>
      <div>
        <div className="footer-content">
          <a href="/about">Amovent.com &#169; 2015-{new Date().getFullYear()}</a>
          <br />
          <a href="#agreement" onClick={(e) => { e.preventDefault(); handleAgreementClick(); }}>{translations["footer.userAgreement"]}</a>
          <br />
          <select id="lang" value={lang} onChange={handleLangChange}>
            <option value="de">🇩🇪 Deutsch</option>
            <option value="en">🇬🇧 English</option>
            <option value="es">🇪🇸 Español</option>
            <option value="ru">🇷🇺 Русский</option>
          </select>
        </div>
      </div>
      <div>
        <div className="footer-content">
          <a href="mailto:a.ryzhkou@gmail.com?Subject=AmoventSupport"
             target="_top">{translations["message.developer"]}</a>
          <br />
          <a href="https://bitbucket.org/pbarbarian/gameteam/issues?status=new&status=open"
             target="_blank" rel="noreferrer">{translations["message.product.developing"]}</a>
        </div>
      </div>
      {!onAgreementClick && (
        <UserAgreementModal
          open={showAgreement}
          onClose={() => setShowAgreement(false)}
          title={translations["footer.userAgreement"]}
        />
      )}
    </footer>
  );
};

export default Footer;